// Angular
import { Injectable, EventEmitter, OnDestroy, OnInit } from '@angular/core';

// Libraries
import { BehaviorSubject } from 'rxjs';
import * as objectPath from 'object-path';
import { merge } from 'lodash-es';

// Models
import { AppCleanup } from '../models';
import { LayoutConfigModel } from '../models/layout-config.model';

// Config
import { LayoutConfig } from '../config/layout.config';

// Enums
import { TenantIdType } from '../enums';
import { TenantDomainType } from '../enums';

@Injectable()
export class LayoutConfigService extends AppCleanup implements OnInit, OnDestroy {
	localLayoutConfig: LayoutConfigModel;
	localLayoutConfig$: BehaviorSubject<LayoutConfigModel> = new BehaviorSubject<LayoutConfigModel>(null);
	showEdit = false;
	isEditing = false;
	isEditing$ = new BehaviorSubject(false);
	blankLayout = false;
	asideDisplay = true;
	desktopHeaderDisplay = true;
	mobileHeaderDisplay = true;
	editEvent: EventEmitter<boolean> = new EventEmitter();
	protected isAsleep: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	isAsleep$ = this.isAsleep.asObservable();
	protected detectScreenChangeVar: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
	detectScreenChange$ = this.detectScreenChangeVar.asObservable();
	domain = window.location.hostname;

	constructor() {
		super();
	}

	ngOnInit() {}

	ngOnDestroy() {
		super.ngOnDestroy();
	}

	saveConfigToLocalStorage(layoutConfig: LayoutConfigModel) {
		if (layoutConfig) {
			localStorage.setItem('layoutConfig', JSON.stringify(layoutConfig));
		}
	}

	getConfigFromLocalStorage(): LayoutConfigModel {
		const config = localStorage.getItem('layoutConfig');
		try {
			return JSON.parse(config);
		} catch (e) {}
	}

	removeConfigFromLocalSotrage() {
		localStorage.removeItem('layoutConfig');
	}

	setConfig(value: any, save?: boolean) {
		this.localLayoutConfig = merge(this.localLayoutConfig, value);

		if (save) {
			this.saveConfigToLocalStorage(this.localLayoutConfig);
		}
	}

	getConfig(path?: string, tenantId?: number): LayoutConfigModel | any {
		// merge default layout config with the saved config from layout storage
		// TODO; known issue; viewing 2 or more demos at the time in different browser's tabs, can cause conflict to the layout config
		let layoutConfig: LayoutConfigModel;

		if (tenantId) {
			layoutConfig = this.getConfigByTenantId(tenantId);
		} else {
			layoutConfig = new LayoutConfig().getDefaultConfigs;
		}

		if (path) {
			// if path is specified, get the value within object
			return objectPath.get(layoutConfig, path);
		}

		return layoutConfig;
	}

	setCurrentConfigByTenantId(tenantId: number): LayoutConfigModel | any {
		if (tenantId) {
			this.localLayoutConfig = this.getConfigByTenantId(tenantId);
			this.localLayoutConfig$.next(this.localLayoutConfig);
		}
	}

	setCurrentConfigByDomain() {
		switch (this.domain) {
			case TenantDomainType.CUDD: {
				this.localLayoutConfig = new LayoutConfig().getCuddConfigs;
				this.localLayoutConfig$.next(this.localLayoutConfig);
				break;
			}

			case TenantDomainType.TTS: {
				this.localLayoutConfig = new LayoutConfig().getTtsConfigs;
				this.localLayoutConfig$.next(this.localLayoutConfig);
				break;
			}

			default:
				this.localLayoutConfig = new LayoutConfig().getDefaultConfigs;
				this.localLayoutConfig$.next(this.localLayoutConfig);
				break;
		}
	}

	setCurrentConfigToDefault() {
		this.localLayoutConfig = new LayoutConfig().getDefaultConfigs;
		this.localLayoutConfig$.next(this.localLayoutConfig);
	}

	getCurrentConfig(path?: string): LayoutConfigModel | any {
		if (path) {
			// if path is specified, get the value within object
			return objectPath.get(this.localLayoutConfig, path);
		}

		return this.localLayoutConfig;
	}

	getConfigByTenantId(tenantId): LayoutConfigModel {
		switch (tenantId) {
			case TenantIdType.CUDD: {
				return new LayoutConfig().getCuddConfigs;
			}

			case TenantIdType.TTS: {
				return new LayoutConfig().getTtsConfigs;
			}

			default:
				return new LayoutConfig().getDefaultConfigs;
		}
	}

	getLogoUrl(tenantId: number): string {
		const layoutConfig: LayoutConfigModel = this.getConfigByTenantId(tenantId);
		const menuAsideLeftSkin: string = objectPath.get(layoutConfig, 'brand.self.skin');
		const logoObject: any = objectPath.get(layoutConfig, 'self.logo'); // set brand logo
		let logo: string = undefined;

		if (typeof logoObject === 'string') {
			logo = logoObject;
		} else if (typeof logo === 'undefined' || !logoObject) {
			try {
				const logos = objectPath.get(layoutConfig, 'self.logo');
				logo = logos[Object.keys(logos)[0]];
			} catch (e) {}
		} else if (typeof logoObject === 'object') {
			logo = objectPath.get(logoObject, menuAsideLeftSkin)?.default;
		}

		return logo;
	}

	getStickyLogo(tenantId: number): string {
		let layoutConfig = this.getConfigByTenantId(tenantId);
		let logo = objectPath.get(layoutConfig, 'self.logo.sticky');
		if (typeof logo === 'undefined') {
			logo = this.getLogoUrl(tenantId);
		}

		return logo + '';
	}

	getLoaderLogo(tenantId: number): string {
		let layoutConfig = this.getConfigByTenantId(tenantId);
		const logoObject = objectPath.get(layoutConfig, 'loader.logo');
		return logoObject;
	}

	loadConfigs(config: LayoutConfigModel) {
		this.localLayoutConfig = this.getConfigFromLocalStorage();
		// use saved config as priority, or load new config if demo does not matched
		if (!this.localLayoutConfig) {
			this.localLayoutConfig = config;
		}
		this.saveConfigToLocalStorage(this.localLayoutConfig);
	}

	reloadConfigs(): LayoutConfigModel {
		this.localLayoutConfig = this.getConfigFromLocalStorage();
		return this.localLayoutConfig;
	}

	setMobileSleep() {
		this.isAsleep.next(true);
	}

	setMobileAwake() {
		this.isAsleep.next(false);
	}

	detectScreenChange() {
		this.detectScreenChangeVar.next(true);
	}
}
